import { DocumentNode, InMemoryCache, TypedDocumentNode } from '@apollo/client';
import { awsDateField, awsDateTimeField, awsJsonField, connectionField, onSubscriptionData as onSubscriptionDataBase, subscribe as subscribeBase, TSubscriptionMapping } from '@chocolate-soup-inc/cs-api-consumer-utils';
import {
  AddressFieldsFragmentDoc,
  CompanyFieldsFragmentDoc,
  DependantFieldsFragmentDoc,
  EventFieldsFragmentDoc,
  GiftFieldsFragmentDoc,
  GiftPackageFieldsFragmentDoc,
  ListAllAddressesDocument,
  ListAllCompaniesDocument,
  ListAllEmployeesDocument,
  ListAllEmployeesFieldsFragmentDoc,
  ListAllOfficesDocument,
  ListCompanyOfficesDocument,
  ListDependantsDocument,
  ListEmployeeAndDependatsGiftsDocument,
  ListEmployeeDependantsDocument,
  ListEventsDocument,
  ListGiftPackagesDocument,
  ListGiftsAssemblingDocument,
  ListNotDoneGiftsDocument,
  ListNotSkippedEventsWithoutGiftDocument,
  ListOneCompanyDocument,
  ListOneCompanyEmployeesDocument,
  ListOneCompanyGiftPackagesDocument,
  ListOneEmployeeDocument,
  ListOneGiftDocument,
  ListOneGiftPackageDocument,
  ListOneOfficeEmployeesDocument,
  ListOneShipmentGiftsDocument,
  ListOneSubTypeGiftPackagesDocument,
  ListOneTypeGiftPackagesDocument,
  ListOpenShipmentsDocument,
  ListOpenShipmentsNewDocument,
  ListProductsDocument,
  ListRecentlyDoneGiftsDocument,
  ListRosterFilesDocument,
  ListSetForPickupShipmentsDocument,
  ListShipmentGiftsDocument,
  ListShipmentsByAddressAndStatusDocument,
  ListShipmentsTrackingNewDocument,
  ListShippedShipmentsDocument,
  ListSkippedEventsDocument,
  ListSkippedEventsNewDocument,
  ListUsersDocument,
  OfficeFieldsFragmentDoc,
  ProductFieldsFragmentDoc,
  RosterFileFieldsFragmentDoc,
  ShipmentNewFieldsAndHydrationFragmentDoc,
  TAddress,
  TCompany,
  TDependant,
  TEmployee,
  TEvent,
  TGift,
  TGiftPackage,
  TLog,
  TOffice,
  TProduct,
  TRosterFile,
  TShipment,
  TUser,
  UserFieldsFragmentDoc
} from '../generated/graphql';
import { client } from './api';

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      queryType: true,
      fields: {
        listAddresses: connectionField<TAddress>({ keyArgs: false }),
        listCompanies: connectionField<TCompany>({ keyArgs: false }),
        listDependants: connectionField<TDependant>({ keyArgs: false }),
        listEmployees: connectionField<TEmployee>({ keyArgs: false }),
        listUsers: connectionField<TUser>({ keyArgs: ['companyId'] }),
        listUsersIncludingCSUsers: connectionField<TUser>({ keyArgs: ['companyId'] }),
        listNotSkippedEventsWithoutGift: connectionField<TEvent>({ keyArgs: false }),
        listSkippedEvents: connectionField<TEvent>({ keyArgs: false }),
        listNotDoneGifts: connectionField<TGift>({ keyArgs: false }),
        listEmployeeAndDependatsGifts: connectionField<TGift>({ keyArgs: false }),
        listRecentlyDoneGifts: connectionField<TGift>({ keyArgs: false }),
        listShipmentGifts: connectionField<TGift>({ keyArgs: false }),
        listOffices: connectionField<TOffice>({ keyArgs: false }),
        listRosterFiles: connectionField<TRosterFile>({ keyArgs: false }),
        listOpenShipments: connectionField<TShipment>({ keyArgs: false }),
        listProducts: connectionField<TProduct>({ keyArgs: false }),
        //listGiftPackages: connectionField<TGiftPackage>({ keyArgs: false }),
        listShippedShipments: connectionField<TShipment>({ keyArgs: false }),
        listSetForPickupShipments: connectionField<TShipment>({ keyArgs: false }),
        listShipmentsByAddressAndStatus: connectionField<TShipment>({ keyArgs: ['addressId', 'status'] }),
        // === NEW ===
        // Users
        listAdminCognitoUsers: awsJsonField({ keyArgs: ['userPoolId'] }),
        // Logs
        listAllLogs: connectionField<TLog>({ keyArgs: ['userType', 'occurredAt', 'filter'] }),
        // Shipments
        listOpenShipmentsNEW: connectionField<TShipment>({ keyArgs: ['filter'] }),
        listOneShipment: connectionField<TShipment>({ keyArgs: ['id', 'filter'] }),
        listShipmentsTrackingNEW: connectionField<TShipment>({ keyArgs: ['shippedAt', 'filter'] }),
        // Gifts
        listGiftsAssembling: connectionField<TGift>({ keyArgs: ['type', 'isDone', 'filter'] }),
        listOneGift: connectionField<TGift>({ keyArgs: ['id', 'filter'] }),
        listOneShipmentGifts: connectionField<TGift>({ keyArgs: ['shipmentId', 'filter'] }),
        // Events
        listEvents: connectionField<TEvent>({ keyArgs: ['type', 'eventPage', 'filter'] }),
        listClientOneOffEvents: connectionField<TEvent>({
          keyArgs: ['eventPage', 'includingLastDays', 'includingNextDays', 'filter', 'order']
        }),
        listSkippedEventsNew: connectionField<TEvent>({ keyArgs: ['eventPage', 'filter'] }),
        // Employees
        listAllEmployees: connectionField<TEvent>({ keyArgs: ['isScan', 'filter'] }),
        listOneEmployee: connectionField<TEvent>({ keyArgs: ['id', 'filter'] }),
        listOneOfficeEmployees: connectionField<TEvent>({ keyArgs: ['officeId', 'filter'] }),
        listOneCompanyEmployees: connectionField<TEvent>({ keyArgs: ['companyId', 'filter'] }),
        // Gift Packages
        listGiftPackages: connectionField<TGiftPackage>({ keyArgs: ['filter'] }),
        listOneGiftPackage: connectionField<TGiftPackage>({ keyArgs: ['id', 'filter'] }),
        listOneCompanyGiftPackages: connectionField<TGiftPackage>({ keyArgs: ['companyId', 'filter'] }),
        listOneTypeGiftPackages: connectionField<TGiftPackage>({ keyArgs: ['type', 'filter'] }),
        listOneSubTypeGiftPackages: connectionField<TGiftPackage>({ keyArgs: ['subType', 'filter'] })
        // === NEW ===
      }
    },
    User: {
      keyFields: ['email']
    },
    Company: {
      keyFields: ['id'],
      fields: {
        startDate: awsDateField(),
        endDate: awsDateField(),
        giftPacks: awsJsonField()
      }
    },
    Dependant: {
      keyFields: ['id'],
      fields: {
        birthDate: awsDateField()
      }
    },
    Employee: {
      keyFields: (obj) => (!obj.id ? false : ['id']),
      fields: {
        hireDate: awsDateField(),
        birthDate: awsDateField(),
        createdAt: awsDateTimeField(),
        updatedAt: awsDateTimeField()
      }
    },
    Event: {
      //keyFields: ['id'],
      fields: {
        eventDate: awsDateField(),
        skippedAt: awsDateTimeField()
        //shippingDate: awsDateField(),
        //referenceEventDate: awsDateField(),
      }
    },
    Gift: {
      keyFields: ['id'],
      fields: {
        //shippingDate: awsDateField(),
        //referenceEventDate: awsDateField(),
        eventDate: awsDateField(),
        recipientData: awsJsonField(),
        groupedGifts: awsJsonField()
      }
    },
    GiftPackage: {
      keyFields: ['id'],
      fields: {
        hydratedProducts: awsJsonField()
      }
    },
    Shipment: {
      keyFields: ['id'],
      fields: {
        shippingDate: awsDateField(),
        giftPacks: awsJsonField()
      }
    },
    Log: {
      keyFields: ['id'],
      fields: {
        arguments: awsJsonField(),
        response: awsJsonField(),
        variables: awsJsonField(),
        identity: awsJsonField()
      }
    },
    Address: {
      keyFields: ['id'],
      fields: {
        parent: awsJsonField()
      }
    }
  },
  possibleTypes: {
    Recipient: ['Employee', 'Dependant']
  }
});

const subscriptionMappings: Record<string, TSubscriptionMapping> = {
  onAnyAddressChanged: {
    listQueries: [ListAllAddressesDocument],
    fragment: AddressFieldsFragmentDoc
  },
  onAnyCompanyChanged: {
    listQueries: [ListAllCompaniesDocument, ListOneCompanyDocument],
    fragment: CompanyFieldsFragmentDoc
  },
  onAnyDependantChanged: {
    listQueries: [ListDependantsDocument, ListEmployeeDependantsDocument],
    fragment: DependantFieldsFragmentDoc
  },
  onAnyEventChanged: {
    listQueries: [
      ListNotSkippedEventsWithoutGiftDocument,
      ListSkippedEventsDocument,
      // === NEW ===
      ListEventsDocument,
      ListSkippedEventsNewDocument
    ],
    fragment: EventFieldsFragmentDoc
  },
  onAnyEmployeeChanged: {
    listQueries: [
      // === NEW ===
      ListAllEmployeesDocument,
      // ListAllEmployeesNewDocument,
      ListOneEmployeeDocument,
      ListOneOfficeEmployeesDocument,
      ListOneCompanyEmployeesDocument
    ],
    fragment: ListAllEmployeesFieldsFragmentDoc
  },
  onAnyGiftChanged: {
    listQueries: [
      ListEmployeeAndDependatsGiftsDocument,
      ListNotDoneGiftsDocument,
      ListRecentlyDoneGiftsDocument,
      ListShipmentGiftsDocument,
      // === NEW ===
      ListGiftsAssemblingDocument,
      ListOneGiftDocument,
      ListOneShipmentGiftsDocument
    ],
    fragment: GiftFieldsFragmentDoc
  },
  onAnyGiftPackageChanged: {
    listQueries: [ListGiftPackagesDocument, ListOneGiftPackageDocument, ListOneCompanyGiftPackagesDocument, ListOneTypeGiftPackagesDocument, ListOneSubTypeGiftPackagesDocument],
    fragment: GiftPackageFieldsFragmentDoc
  },
  onAnyOfficeChanged: {
    listQueries: [ListAllOfficesDocument, ListCompanyOfficesDocument],
    fragment: OfficeFieldsFragmentDoc
  },
  onAnyProductChanged: {
    listQueries: [ListProductsDocument],
    fragment: ProductFieldsFragmentDoc
  },
  onAnyRosterFileChanged: {
    listQueries: [ListRosterFilesDocument],
    fragment: RosterFileFieldsFragmentDoc
  },
  onAnyShipmentChanged: {
    listQueries: [
      ListOpenShipmentsDocument,
      ListShippedShipmentsDocument,
      ListSetForPickupShipmentsDocument,
      ListShipmentsByAddressAndStatusDocument,
      // === NEW ===
      ListOpenShipmentsNewDocument,
      ListShipmentsTrackingNewDocument
    ],
    fragment: ShipmentNewFieldsAndHydrationFragmentDoc
  },
  onCompanyUserChanged: {
    listQueries: [ListUsersDocument],
    fragment: UserFieldsFragmentDoc
  }
};

export const onSubscriptionData = onSubscriptionDataBase({
  cache,
  subscriptionMappings,
  debug: import.meta.env.REACT_APP_STAGE != 'production'
});

export type TSubscribeData = {
  query: DocumentNode | TypedDocumentNode;
  variables?: null | Record<string, any>;
};

export const subscribe = ({ query, variables }: TSubscribeData) => {
  return subscribeBase(
    {
      client,
      query,
      onSubscriptionData,
      variables
    },
    import.meta.env.REACT_APP_STAGE != 'production'
  );
};
